import BaseTibbit from '@appblocks/codegeneration/Tibbits/_BaseTibbit';
import Tibbits from '@appblocks/codegeneration/Tibbits/index';
import {
    CodeBlock,
    BlockType,
    SettingType,
    SettingState,
    Setting,
    ValidationMessage,
    ValidationMessageType,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';

export default class Tibbit_61_1 extends BaseTibbit {
    constructor() {
        super();
        this.id = 'tibbit_61_1';
        this.name = 'Tibbit 61-1';
        this.files = [
            'libraries/ssi/ssi.tbh',
            'libraries/ssi/ssi.tbs',
            'libraries/super_i2c/super_i2c.tbh',
            'libraries/super_i2c/super_i2c.tbs',
            'libraries/tibbits/tbt61/tbt61.tbh',
            'libraries/tibbits/tbt61/tbt61.tbs',
        ];
        const settings: Setting[] = [
            {
                id: 'GENERAL',
                name: 'Configuration',
                settingType: SettingType.GROUP,
                default: '',
                required: true,
            },
            {
                id: 'ADVANCED',
                name: 'Advanced',
                settingType: SettingType.GROUP,
                default: '',
                required: true,
            },
            {
                id: 'NAME',
                name: 'Name',
                settingType: SettingType.NAME,
                group: 'GENERAL',
                default: '',
                required: true,
            },
            {
                id: 'MODE',
                name: 'Voltage Inputs Mode',
                settingType: SettingType.OPTION,
                required: true,
                group: 'GENERAL',
                default: 'DIFF',
                helpText: `
Tibbit #61-1 offers two differential ±10V voltage inputs, which are recommended over single-ended inputs due to their high noise rejection capability and wider input range. For differential inputs, you do not need to connect the isolated ground between the sensor and the Tibbit. To reduce noise, you can connect the signal cable's shield to the isolated ground (terminal block pins #5 or #9). When connecting the isolated ground, care must be taken to ensure that the common-mode voltage of the positive and negative inputs does not exceed ±20V. Protection is in place to prevent damage, but prolonged exposure to excessive common-mode voltage may cause degradation.

Tibbit #61-1 also offers four single-ended channels capable of reading 0–10V analog inputs with reference to the isolated ground (terminal block pins #5 and #9). If additional channels are needed, the Tibbit's voltage inputs can be used as single-ended by connecting the signals appropriately and configuring the Tibbit mode via software library functions. However, increasing the number of channels comes at the cost of reduced input voltage range, signal-to-noise ratio (SNR), and precision. In single-ended mode, the isolated ground pin (terminal block pins #5 and #9) must be connected.


                `,
                options: [
                    {
                        name: 'Differential Mode',
                        value: 'DIFF',
                    },
                    {
                        name: 'Single Ended Mode',
                        value: 'SINGLE',
                    },
                ],
            },
            {
                id: 'VIN1P',
                name: 'VIN1+(Input)',
                settingType: SettingType.NAME,
                dataType: TibboDataType.FLOAT,
                required: true,
                group: 'GENERAL',
                helpText: `
- Differential Voltage Input channel 1 (+)
- Single-ended Voltage Input channel 1 (+)                
                `,
                default: '',
            },
            {
                id: 'VIN1M',
                name: 'VIN1-(Input)',
                settingType: SettingType.NAME,
                dataType: TibboDataType.FLOAT,
                required: true,
                group: 'GENERAL',
                helpText: `
- Differential Voltage Input channel 1 (-)
- Single-ended Voltage Input channel 1 (-)                
                `,
                default: '',
            },
            {
                id: 'VIN2P',
                name: 'VIN2+(Input)',
                settingType: SettingType.NAME,
                dataType: TibboDataType.FLOAT,
                required: true,
                group: 'GENERAL',
                helpText: `
- Differential Voltage Input channel 2 (+)
- Single-ended Voltage Input channel 2 (+)                
                `,
                default: '',
            },
            {
                id: 'VIN2M',
                name: 'VIN2-(Input)',
                settingType: SettingType.NAME,
                dataType: TibboDataType.FLOAT,
                required: true,
                group: 'GENERAL',
                helpText: `
- Differential Voltage Input channel 2 (-)
- Single-ended Voltage Input channel 2 (-)                
                `,
                default: '',
            },
            {
                id: 'IIN1',
                name: 'IIN1(Input)',
                settingType: SettingType.NAME,
                dataType: TibboDataType.FLOAT,
                required: true,
                group: 'GENERAL',
                helpText: '4-20mA Current Input 1',
                default: '',
            },
            {
                id: 'IIN2',
                name: 'IIN2(Input)',
                settingType: SettingType.NAME,
                dataType: TibboDataType.FLOAT,
                required: true,
                group: 'GENERAL',
                helpText: '4-20mA Current Input 2',
                default: '',
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getState(settingId: string): SettingState {
        const MODE = this.getSettingValue('MODE');
        if (settingId === 'VIN1M' || settingId === 'VIN2M') {
            return MODE === 'DIFF' ? SettingState.HIDDEN : SettingState.ENABLED;
        }
        return SettingState.ENABLED;
    }

    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];
        const project: ProjectConfiguration = this.project;
        const tibbit_43_1_node = project.getFeature('node_tibbit_43_1') as BaseNode;
        if (tibbit_43_1_node !== undefined) {
            if (this.getSettingValue('MODE').toString() !== 'COMMAND_MODE') {
                errors.push({ message: 'Data Mode not supported by AppBlocks', messageType: ValidationMessageType.ERROR });
            }
        }
        const tibbit_43_2_node = project.getFeature('node_tibbit_43_2') as BaseNode;
        if (tibbit_43_2_node !== undefined) {
            if (this.getSettingValue('MODE').toString() !== 'COMMAND_MODE') {
                errors.push({ message: 'Data Mode not supported by AppBlocks', messageType: ValidationMessageType.ERROR });
            }
        }
        return errors;
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];

        if (this.project.runtime === 'simulator') {
            return [];
        }

        let socket = Tibbits.getSocket(this.getSettingValue('SOCKET'), this.project.device.id);
        if (!socket) {
            return blocks;
        }
        const currentSocket = socket.socket;
        socket = Tibbits.getSocket((Number(this.getSettingValue('SOCKET')) + 2).toString(), this.project.device.id);
        if (!socket) {
            return blocks;
        }
        const nextSocket = socket.socket;

        const NAME = this.getSettingValue('NAME');
        const MODE = this.getSettingValue('MODE');
        const VIN1P = this.getSettingValue('VIN1P');
        const VIN1M = this.getSettingValue('VIN1M');
        const VIN2P = this.getSettingValue('VIN2P');
        const VIN2M = this.getSettingValue('VIN2M');
        const IIN1 = this.getSettingValue('IIN1');
        const IIN2 = this.getSettingValue('IIN2');

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare ${NAME}_CHANNEL_NUM as byte
        `,
        });

        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            template: `
dim ${NAME}_CHANNEL_NUM as byte
        `,
        });

        const csPin = Tibbits.getSocketPins(currentSocket, this.project.device.id)[0];
        const clkPin = Tibbits.getSocketPins(currentSocket, this.project.device.id)[1];
        const mosiPin = Tibbits.getSocketPins(currentSocket, this.project.device.id)[2];
        const misoPin = Tibbits.getSocketPins(currentSocket, this.project.device.id)[3];
        const sclPin = Tibbits.getSocketPins(nextSocket, this.project.device.id)[0];
        const sdaPin = Tibbits.getSocketPins(nextSocket, this.project.device.id)[1];


        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
    dim ${NAME}_init_result as TBT61_INIT_RESULT

    ' INITIALIZE

    sys.debugprint("TBT61 initialize - ")

    ${NAME}_init_result = tbt61_init("${NAME}", ${MODE === 'DIFF' ? 'TBT61_VOLTAGE_TWO_DIFFERENTIAL' : 'TBT61_VOLTAGE_FOUR_SINGLE'}, 
    ${csPin}, ${clkPin}, ${mosiPin}, ${misoPin}, ${sclPin}, ${sdaPin}, ${NAME}_CHANNEL_NUM)


    select case ${NAME}_init_result
    case TBT61_INIT_OK:
        sys.debugprint("Init complete" + chr(13))
        
    case TBT61_INIT_ERROR_SPI_NO_EMPTY_CHANNEL:
        sys.debugprint("Init fault: SPI not available channel" + chr(13))
        
    case TBT61_INT_ERROR_NO_TIBBIT_PRESENT:
        sys.debugprint("Init fault: Tibbit not present" + chr(13))
        
    case TBT61_INIT_ERROR_NO_VALID_VERSION:
        sys.debugprint("Init fault: HW version wrong" + chr(13))
        
    case TBT61_INIT_ERROR_FAULT_CHECK_POWER:
        sys.debugprint("Init fault: Internal voltage check fault" + chr(13))
        
    end select

    sys.debugprint("--------End of Sys_Init--------" + chr(13))
    sys.debugprint("***************************************" + chr(13))
    `,
        });

        if (MODE === 'DIFF') {
            blocks.push({
                location: `var_${VIN1P}_update`,
                blockType: BlockType.CODE,
                template: `
    dim flags as byte
    var_${VIN1P}_update_completed(tbt61_read_adc(${NAME}_CHANNEL_NUM, VIN1_DIFFERENTIAL, flags))
            `,
            });

            blocks.push({
                location: `var_${VIN2P}_update`,
                blockType: BlockType.CODE,
                template: `
    dim flags as byte
    var_${VIN2P}_update_completed(tbt61_read_adc(${NAME}_CHANNEL_NUM, VIN2_DIFFERENTIAL, flags))
            `,
            });
        } else {
            blocks.push({
                location: `var_${VIN1P}_update`,
                blockType: BlockType.CODE,
                template: `
    dim flags as byte
    var_${VIN1P}_update_completed(tbt61_read_adc(${NAME}_CHANNEL_NUM, VIN1P_SINGLE_ENDED, flags))
            `,
            });

            blocks.push({
                location: `var_${VIN1M}_update`,
                blockType: BlockType.CODE,
                template: `
    dim flags as byte
    var_${VIN1M}_update_completed(tbt61_read_adc(${NAME}_CHANNEL_NUM, VIN1M_SINGLE_ENDED, flags))
            `,
            });

            blocks.push({
                location: `var_${VIN2P}_update`,
                blockType: BlockType.CODE,
                template: `
    dim flags as byte
    var_${VIN2P}_update_completed(tbt61_read_adc(${NAME}_CHANNEL_NUM, VIN2P_SINGLE_ENDED, flags))
            `,
            });

            blocks.push({
                location: `var_${VIN2M}_update`,
                blockType: BlockType.CODE,
                template: `
    dim flags as byte
    var_${VIN2M}_update_completed(tbt61_read_adc(${NAME}_CHANNEL_NUM, VIN2M_SINGLE_ENDED, flags))
            `,
            });
        }

        blocks.push({
            location: `var_${IIN1}_update`,
            blockType: BlockType.CODE,
            template: `
dim flags as byte 
var_${IIN1}_update_completed(tbt61_read_adc(${NAME}_CHANNEL_NUM, IIN1_SINGLE_ENDED, flags))
        `,
        });

        blocks.push({
            location: `var_${IIN2}_update`,
            blockType: BlockType.CODE,
            template: `
dim flags as byte 
var_${IIN2}_update_completed(tbt61_read_adc(${NAME}_CHANNEL_NUM, IIN2_SINGLE_ENDED, flags))
        `,
        });

        return blocks;
    }
}
